import { isEmptyOrSpace } from '../../other/ui/string-helper-functions.js';
import { LangV6Config } from '../../../localization/scripts/v6config/v6config-lang.js';
import type { StringDictionary } from '../types/misc.js';

/**
 * This is a light independant expectation of a "FRAME" in dealer that it can use to process and report on information
 * This provides a decoupled layer between webmodule and v6config or any other future config
 */
export enum FrameValidationType {
  nothing = 0,
  note = 1,
  information = 2,
  warning = 3,
  critical = 4,
  outOfDate = 5
}

export interface FrameValidation {
  type: FrameValidationType;
  title: string;
  visible?: boolean;
  text?: string;
  location?: FramePoint;
  locationGlobal?: FramePoint;
}

export interface FramePoint {
  x: number;
  y: number;
}

export interface FrameViewBox {
  minx: number;
  miny: number;
  width: number;
  height: number;
}

export enum FrameAttributeEditorType {
  Text = 1,
  Dropdown = 2,
  Picker = 3,
  Length = 4,
  Currency = 5,
  Number = 6,
  Boolean = 7,
  Dimension = 8,
  Glazing = 9,
  Macro = 10,
  Quantity = 11,

  RootFrameDimension = 12,
  MacroEqualizeLights = 13,
  BoolTrigger = 14,
  CommandMacro = 15,
  WholeNumber = 16,

  PassthroughValue = 1000,
  CombinationGlazing = 999,
  QuoteOptionProperty = 8000
}

export interface FrameAttributeTemplateValue {
  groupName: string;
  groupDescription: string;
  itemIndex: number;
  code: string;
  description: string;
  dataType: FrameAttributeEditorType;
  value: string;
  displayValue: string;
}
export interface FrameAttributeTemplate {
  frameType: string;
  name: string;

  attributes: FrameAttributeTemplateValue[];
}

export interface FrameAttribute {
  code: string;
  dataType: FrameAttributeEditorType;
  value: string;
  displayValue: string;
  description: string;
  hideFromReport?: boolean;
  hideFromUI?: boolean;
}
export enum FrameAttributeGroupType {
  QuoteItem = 0,
  Frame = 1,
  Quote = 2,
  Validations = 3,
  Igu = 4,
  QuoteAttributeItem = 5
}
export interface FrameAttributeGroup {
  groupType: FrameAttributeGroupType;
  name: string;
  description: string;
  hideFromReport?: boolean;
  hideFromUI?: boolean;
  attributes: FrameAttribute[];
}

export interface FrameBuyInItem {
  resourceType: number;
  code: string;
  partCode: string;
  decription: string;
  quantity: number;
  extraDetails: StringDictionary | null;
}

export interface Frame {
  productLine?: string | null;
  description: string;
  boundary: FrameViewBox;
  lightName?: string;
  attributeGroups: FrameAttributeGroup[];
  nestedFrames: Frame[];
  validations?: FrameValidation[];
  buyIn?: FrameBuyInItem[];
}

// --- Frame compare types and utilities ---

export type DiffStatus = 'added' | 'removed' | 'changed' | 'unchanged';

export interface FrameAttributeDiff {
  code: string;
  description?: string | null; // like label
  value: { before: string | null; after: string | null };
  displayValue: { before: string | null; after: string | null };
  status: DiffStatus;
}

export interface FrameCompareReport {
  lightName?: { before: string | null; after: string | null } | null;
  description?: { before: string | null; after: string | null } | null;
  attributes: FrameAttributeDiff[];
  nested: FrameCompareReport[]; // children
  frameStatus?: 'added' | 'removed' | 'changed' | 'unchanged';
}

function flattenFrameAttributes(groups: FrameAttributeGroup[] | null | undefined): Map<string, FrameAttribute> {
  const map = new Map<string, FrameAttribute>();
  if (!groups) return map;
  for (const g of groups) {
    if (!g || !g.attributes) continue;
    for (const a of g.attributes) {
      if (!a || !a.code) continue;
      map.set(a.code, a);
    }
  }
  return map;
}

function attributeStatus(before: FrameAttribute | undefined, after: FrameAttribute | undefined): DiffStatus {
  if (!before && after) return 'added';
  if (before && !after) return 'removed';
  if (!before && !after) return 'unchanged';
  // both exist: compare effective values (value then displayValue)
  const beforeVal = before?.value ?? '';
  const afterVal = after?.value ?? '';
  const beforeDisp = before?.displayValue ?? '';
  const afterDisp = after?.displayValue ?? '';

  // If both raw values are empty but displayValue matches, treat as unchanged (sanitizer case)
  if (beforeVal === '' && afterVal === '' && beforeDisp === afterDisp) return 'unchanged';

  // If values don't match but displayValues do match, and after value is empty, this is sanitized data
  if (beforeVal !== afterVal && beforeDisp === afterDisp && afterVal === '') return 'unchanged';

  const eff = (x: FrameAttribute | undefined) => {
    if (!x) return '';
    const v = x.value ?? '';
    const dv = x.displayValue ?? '';
    if (v !== '') return v;
    return dv;
  };
  return eff(before) !== eff(after) ? 'changed' : 'unchanged';
}

export interface FrameCompareOptions {
  /** When true, attributes or groups flagged hideFromReport/hideFromUI are excluded from diff */
  excludeHidden?: boolean;
}

export function compareFrames(
  before: Frame | null | undefined,
  after: Frame | null | undefined,
  options?: FrameCompareOptions
): FrameCompareReport | null {
  if (!before && !after) return null;

  const lightBefore = before?.lightName ?? null;
  const lightAfter = after?.lightName ?? null;

  // Determine frame status
  let frameStatus: 'added' | 'removed' | 'changed' | 'unchanged';
  if (!before && after) {
    frameStatus = 'added';
  } else if (before && !after) {
    frameStatus = 'removed';
  } else {
    frameStatus = 'changed'; // Will be determined later based on content
  }

  // If different identity (lightName differs and both present) then report minimal
  if (lightBefore && lightAfter && lightBefore !== lightAfter) {
    return {
      lightName: { before: lightBefore, after: lightAfter },
      description: { before: before?.description ?? null, after: after?.description ?? null },
      attributes: [],
      nested: [],
      frameStatus: 'changed'
    };
  }

  // Compare root attributes
  const beforeMap = flattenFrameAttributes(before ? before.attributeGroups : null);
  const afterMap = flattenFrameAttributes(after ? after.attributeGroups : null);
  const allCodes = new Set<string>();
  for (const k of beforeMap.keys()) allCodes.add(k);
  for (const k of afterMap.keys()) allCodes.add(k);

  const attrs: FrameAttributeDiff[] = [];
  const excludeHidden = options?.excludeHidden === true;
  for (const code of Array.from(allCodes).sort()) {
    const b = beforeMap.get(code);
    const a = afterMap.get(code);
    // Skip hidden attributes if requested (either before or after side marks it hidden)
    if (excludeHidden && (b?.hideFromReport || b?.hideFromUI || a?.hideFromReport || a?.hideFromUI)) continue;
    const status = attributeStatus(b, a);
    if (status === 'unchanged') continue;
    attrs.push({
      code,
      description: a?.description ?? b?.description ?? null,
      value: { before: b ? (b.value ?? null) : null, after: a ? (a.value ?? null) : null },
      displayValue: { before: b ? (b.displayValue ?? null) : null, after: a ? (a.displayValue ?? null) : null },
      status
    });
  }

  // Recurse into nested frames — match by lightName where present, otherwise by position
  const beforeNested = before ? before.nestedFrames || [] : [];
  const afterNested = after ? after.nestedFrames || [] : [];

  const nestedReports: FrameCompareReport[] = [];

  const refs = new Set<string>();
  for (const f of beforeNested) if (f && f.lightName) refs.add(f.lightName);
  for (const f of afterNested) if (f && f.lightName) refs.add(f.lightName);

  if (refs.size > 0) {
    for (const r of Array.from(refs).sort()) {
      const b = beforeNested.find(x => x.lightName === r) ?? null;
      const a = afterNested.find(x => x.lightName === r) ?? null;
      const cmp = compareFrames(b, a, options);
      if (cmp) nestedReports.push(cmp);
    }
  } else {
    const max = Math.max(beforeNested.length, afterNested.length);
    for (let i = 0; i < max; i++) {
      const b = beforeNested[i] ?? null;
      const a = afterNested[i] ?? null;
      const cmp = compareFrames(b, a, options);
      if (cmp) nestedReports.push(cmp);
    }
  }

  // Determine final frame status
  if (frameStatus === 'changed') {
    if (attrs.length === 0 && nestedReports.length === 0) {
      frameStatus = 'unchanged';
    }
  }

  return {
    lightName: { before: lightBefore, after: lightAfter },
    description: { before: before?.description ?? null, after: after?.description ?? null },
    attributes: attrs,
    nested: nestedReports,
    frameStatus
  };
}

function escapeMd(v: any): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return s.replace(/\|/g, '\\|').replace(/`/g, '\\`');
}

function normalizeText(input: any): string {
  if (input === null || input === undefined) return '';
  let s = String(input);
  s = s.replace(/\uFFFD/g, '"');
  s = s.replace(/[\u201C\u201D\u201E\u201F]/g, '"');
  s = s.replace(/[\u2018\u2019\u201A\u201B]/g, "'");
  s = s.replace(/\u2026/g, '...');
  s = s.replace(/\u2013/g, '-');
  s = s.replace(/\u2014/g, '--');
  return s;
}

export function isFrameCompareReportEmpty(report: FrameCompareReport | null | undefined): boolean {
  if (!report) return true;
  if (report.attributes && report.attributes.length > 0) return false;
  if (report.nested) {
    for (const n of report.nested) {
      if (!isFrameCompareReportEmpty(n)) return false;
    }
  }
  return true;
}

export function getFrameCompareAsMarkdown(header: string, report?: FrameCompareReport | null): string {
  if (!report || isFrameCompareReportEmpty(report)) {
    return ``;
  }
  const lines: string[] = [];
  if (!isEmptyOrSpace(header)) lines.push(`# ${header}`);
  lines.push('');

  const title = isEmptyOrSpace(header)
    ? undefined
    : (report.description?.after ?? report.description?.before ?? undefined);
  if (title) lines.push(`## ${escapeMd(title)}`);
  lines.push('');

  if (!report.attributes || report.attributes.length === 0) {
    lines.push(LangV6Config.frameCompareNoAttributeChanges());
    lines.push('');
  } else {
    for (const a of report.attributes) {
      const rawLabel = a.description ?? a.code ?? '';
      const label = escapeMd(rawLabel);
      if (a.status === 'added') {
        const val = escapeMd(normalizeText(a.displayValue.after ?? a.value.after ?? ''));
        lines.push(LangV6Config.frameCompareAttributeAdded({ label, value: val }));
      } else if (a.status === 'removed') {
        const val = escapeMd(normalizeText(a.displayValue.before ?? a.value.before ?? ''));
        lines.push(LangV6Config.frameCompareAttributeRemoved({ label, value: val }));
      } else if (a.status === 'changed') {
        const beforeVal = escapeMd(normalizeText(a.displayValue.before ?? a.value.before ?? ''));
        const afterVal = escapeMd(normalizeText(a.displayValue.after ?? a.value.after ?? ''));
        lines.push(LangV6Config.frameCompareAttributeChanged({ label, before: beforeVal, after: afterVal }));
      }
    }
    lines.push('');
  }

  // breadcrumb nested rendering
  function renderNested(nested: FrameCompareReport[], crumbs: string[] = []) {
    for (const nf of nested || []) {
      const current =
        nf.lightName?.after ?? nf.lightName?.before ?? nf.description?.after ?? nf.description?.before ?? '(no ref)';
      const newCrumbs = crumbs.concat([String(current)]);
      const breadcrumb = newCrumbs.join(' > ');

      // If entire frame was removed or added, show concise message
      if (nf.frameStatus === 'removed') {
        lines.push(LangV6Config.frameCompareBreadcrumbTitle({ breadcrumb: escapeMd(breadcrumb) }));
        lines.push(LangV6Config.frameCompareFrameRemoved());
        lines.push('');
        continue; // Skip detailed attribute listing
      } else if (nf.frameStatus === 'added') {
        lines.push(LangV6Config.frameCompareBreadcrumbTitle({ breadcrumb: escapeMd(breadcrumb) }));
        lines.push(LangV6Config.frameCompareFrameAdded());
        lines.push('');
        continue; // Skip detailed attribute listing
      }

      // For changed frames, show detailed changes
      lines.push(LangV6Config.frameCompareBreadcrumbTitle({ breadcrumb: escapeMd(breadcrumb) }));
      if (nf.attributes && nf.attributes.length) {
        for (const a of nf.attributes) {
          const rawLabel = a.description ?? a.code ?? '';
          const label = escapeMd(rawLabel);
          if (a.status === 'added') {
            const val = escapeMd(normalizeText(a.displayValue.after ?? a.value.after ?? ''));
            lines.push(LangV6Config.frameCompareAttributeAdded({ label, value: val }));
          } else if (a.status === 'removed') {
            const val = escapeMd(normalizeText(a.displayValue.before ?? a.value.before ?? ''));
            lines.push(LangV6Config.frameCompareAttributeRemoved({ label, value: val }));
          } else if (a.status === 'changed') {
            const beforeVal = escapeMd(normalizeText(a.displayValue.before ?? a.value.before ?? ''));
            const afterVal = escapeMd(normalizeText(a.displayValue.after ?? a.value.after ?? ''));
            lines.push(LangV6Config.frameCompareAttributeChanged({ label, before: beforeVal, after: afterVal }));
          }
        }
        lines.push('');
      }
      if (nf.nested && nf.nested.length) {
        renderNested(nf.nested, newCrumbs);
      }
    }
  }

  renderNested(report.nested || [], []);

  return lines.join('\n');
}
